import React, { useState, useMemo } from 'react';
import { useNostr } from '@/hooks/useNostr';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAuthor } from '@/hooks/useAuthor';
import { useBulkFollow } from '@/hooks/useBulkFollow';
import { FOLLOW_PACKS } from '@/lib/followPacks';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, UserPlus, Check } from 'lucide-react';
import { nip19 } from 'nostr-tools';

interface SuggestedAccountProps {
  pubkey: string;
  name: string;
  isFollowing: boolean;
  onFollow: (pubkey: string) => void;
  isPending: boolean;
}

function SuggestedAccount({ pubkey, name, isFollowing, onFollow, isPending }: SuggestedAccountProps) {
  const author = useAuthor(pubkey);
  const metadata = author.data?.metadata;
  const npub = nip19.npubEncode(pubkey);

  return (
    <div className="flex items-center gap-2.5 py-2 px-1">
      <img
        src={metadata?.picture || `https://robohash.org/${pubkey}.png?set=set4`}
        alt={name}
        className="size-9 rounded-full object-cover bg-secondary border border-border shrink-0"
        onError={(e) => { (e.target as HTMLImageElement).src = `https://robohash.org/${pubkey}.png?set=set4`; }}
      />
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-bold truncate leading-tight">{metadata?.display_name || metadata?.name || name}</p>
        <p className="text-[10px] text-muted-foreground truncate leading-tight">@{npub.slice(0, 8)}</p>
      </div>
      <Button
        type="button"
        disabled={isPending || isFollowing}
        onClick={() => onFollow(pubkey)}
        className={`rounded-full h-7 px-3 text-[10px] font-bold shrink-0 cursor-pointer ${
          isFollowing
            ? 'bg-muted text-muted-foreground'
            : 'bg-foreground text-background hover:bg-foreground/90'
        }`}
      >
        {isFollowing ? <Check className="size-3 mr-0.5" /> : <UserPlus className="size-3 mr-0.5" />}
        {isFollowing ? 'Followed' : 'Follow'}
      </Button>
    </div>
  );
}

interface SuggestedAccountsProps {
  maxAccounts?: number;
}

export function SuggestedAccounts({ maxAccounts = 4 }: SuggestedAccountsProps) {
  const { user } = useCurrentUser();
  const bulkFollow = useBulkFollow();
  const [followingSet, setFollowingSet] = useState<Set<string>>(new Set());

  // Combine all accounts from all packs
  const allAccounts = useMemo(() => {
    const seen = new Set<string>();
    return FOLLOW_PACKS.flatMap(pack =>
      pack.accounts.filter(a => {
        if (seen.has(a.pubkey)) return false;
        seen.add(a.pubkey);
        return true;
      })
    ).slice(0, maxAccounts);
  }, [maxAccounts]);

  const handleFollow = async (pubkey: string) => {
    setFollowingSet(prev => new Set(prev).add(pubkey));
    await bulkFollow.mutateAsync([pubkey]);
  };

  if (!user) return null;

  return (
    <Card className="rounded-2xl border border-border/80 overflow-hidden shadow-none bg-secondary/20">
      <CardHeader className="p-4 pb-2">
        <CardTitle className="text-xl font-extrabold font-sans">Who to follow</CardTitle>
      </CardHeader>
      <div className="flex flex-col px-4 pb-3 divide-y divide-border/40">
        {allAccounts.map((account) => (
          <SuggestedAccount
            key={account.pubkey}
            pubkey={account.pubkey}
            name={account.name}
            isFollowing={followingSet.has(account.pubkey)}
            onFollow={handleFollow}
            isPending={bulkFollow.isPending}
          />
        ))}
      </div>
    </Card>
  );
}